import dayjs from 'dayjs';

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Ömer Koşar
 */

export const PickupDeliveryReportOperationConfig = {
	id: DYNAMICLISTS.OPERATION_PICKUP_DELIVERY_REPORT,
	columns: [
		{
			Header: 'id',
			accessor: 'ID',
			filterType: FilterType.number,
			width: 50,
			show: false,
			showCopyButton: true
		},
		{
			Header: 'orderID',
			accessor: 'orderID',
			filterType: FilterType.string,
			width: 90,
			showOnMobile: true,
			showCopyButton: true
		},
		{
			Header: 'orderDate',
			accessor: 'createdAt',
			filterType: FilterType.date,
			width: 100,
			renderCell: ListComponents.DATETIME,
			filterRemovable: false
		},
		{
			Header: 'clientName',
			accessor: 'clientName',
			filterType: FilterType.multiSelection,
			filterRemovable: false,
			customFilterOptions: [FilterAction.between, FilterAction.on]
		},
		{ Header: 'name', accessor: 'name', filterType: FilterType.string },
		{
			Header: 'pickupLocation',
			accessor: 'pickupLocation',
			filterType: FilterType.enumMulti,
			enumName: ENUMS.PICKUP_LOCATION,
			width: 90,
			roleName: 'BrandingModule'
		},
		{
			Header: 'city',
			accessor: 'cityName',
			filterType: FilterType.multiSelection
		},
		{
			Header: 'serviceType',
			accessor: 'serviceType',
			filterType: FilterType.multiSelection,
			show: false
		},
		{
			Header: 'shippingAmount',
			accessor: 'shippingAmount',
			filterType: FilterType.float,
			width: 120,
			renderCell: ListComponents.CURRENCY
		},
		{
			Header: 'pickupDate',
			accessor: 'pickupDate',
			filterType: FilterType.date,
			width: 100,
			renderCell: ListComponents.DATETIME
		},
		{ Header: 'preparationTime', accessor: 'preparationTime', filterType: FilterType.string },
		{ Header: 'fulfillmentTime', accessor: 'fulfillmentTime', filterType: FilterType.string },
		{ Header: 'pickupDifferenceHours', accessor: 'pickupDifference', filterType: FilterType.string },
		{
			Header: 'deliveryDate',
			accessor: 'deliveryDate',
			filterType: FilterType.date,
			width: 100,
			renderCell: ListComponents.DATETIME
		},
		{ Header: 'deliveryDifferenceHours', accessor: 'deliveryDifference', filterType: FilterType.string },
		{
			Header: 'shipmentCreatedDate',
			accessor: 'shipmentCreatedDate',
			filterType: FilterType.date,
			width: 100,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'deliveryCompany',
			accessor: 'deliveryName',
			filterType: FilterType.enumMulti,
			width: 90,
			enumName: ENUMS.DELIVERY_COMPANIES_USED,
			renderCell: ListComponents.DC_LOGO
		},
		{
			Header: 'shipmentNumber',
			accessor: 'shipmentNumber',
			filterType: FilterType.number,
			width: 110,
			renderCell: ListComponents.TRACKING_URL
		},
		{ Header: 'distanceString', accessor: 'distanceString', filterType: FilterType.string, show: false },
		{ Header: 'distanceGroup', accessor: 'distanceGroup', filterType: FilterType.string, show: false },
		{ Header: 'zoneName', accessor: 'zoneName', filterType: FilterType.string },
		{ Header: 'tripDifference', accessor: 'tripDifference', filterType: FilterType.string },
		{ Header: 'slaCheck', accessor: 'SLACheck', filterType: FilterType.string },
		{ Header: 'SLA', accessor: 'SLA', filterType: FilterType.string, show: false },
		{
			Header: 'deliverySlotDate',
			accessor: 'deliverySlotDate',
			filterType: FilterType.date,
			renderCell: ListComponents.DATE,
			width: 100,
			show: false
		},
		{
			Header: 'deliverySlotFrom',
			accessor: 'deliverySlotFrom',
			filterType: FilterType.string,
			show: false
		},
		{
			Header: 'deliverySlotTo',
			accessor: 'deliverySlotTo',
			filterType: FilterType.string,
			show: false
		},
		{
			Header: 'status',
			accessor: 'status',
			renderCell: ListComponents.STATUS,
			filterType: FilterType.enumMulti,
			enumName: ENUMS.ALL_STATUSES,
			filterPrefix: 'status.',
			width: 100,
			showOnMobile: true
		}
	],
	fetchUrl: REPORT.LIST.GET_PICKUP_AND_DELIVERY_LIST_OPERATIONS,
	exportName: 'pickupDeliveryReport'
};

const PickupDeliveryReportOperation = () => {
	const { state } = useLocation();

	const defaultSort = useMemo(() => [{ id: 'ID', desc: true }], []);

	const defaultFilters = useMemo(
		() => [
			...(state?.data?.clientName
				? [
						{
							column: PickupDeliveryReportOperationConfig.columns.find(
								(c) => c.accessor === 'clientName'
							),
							action: FilterAction.is,
							value: [state.data.clientName]
						}
					]
				: []),
			{
				column: PickupDeliveryReportOperationConfig.columns.find((c) => c.accessor === 'createdAt'),
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[]
	);
	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					lightTitle="reports"
					boldTitle="pickupDeliveryReport"
					icon={faArrowLeft}
					backUrl={mergePaths(PATHS.REPORTS, PATHS.SHIPMENT_REPORTS)}
				/>
			</HeaderContainerFormHeader>
			<DynamicList
				{...PickupDeliveryReportOperationConfig}
				defaultSort={defaultSort}
				defaultFilters={defaultFilters}
				infiniteScroll
			/>
		</div>
	);
};

export default PickupDeliveryReportOperation;
